import React, { useCallback, useEffect, useState } from 'react';
import Dialog from './Dialog';
import DriverLinks from './DriverLinks';
import TourDetail from './TourDetail';
import { api } from '../services/api';
import { useSignedIn } from '../session';
import {
  Driver,
  ExceptionDecisionOption,
  GoodsInVehicle,
  OpenException,
  PostingToDo,
  TourDetail as Detail,
  TourProgress
} from '../types';


/** Osvežava se sam, jer operater ovaj ekran drži otvoren dok radi nešto drugo. */
const REFRESH_MS = 60_000;

/**
 * Šta se dešava na putu.
 *
 * Vozačev izuzetak bez ovog ekrana je zapis koji niko ne čita. Zato izuzeci
 * stoje na vrhu, pre napretka tura — napredak je informacija, izuzetak je posao
 * koji čeka.
 */
export default function FieldMonitor() {
  const signedIn = useSignedIn();
  const [tours, setTours] = useState<TourProgress[]>([]);
  const [exceptions, setExceptions] = useState<OpenException[]>([]);
  const [decisions, setDecisions] = useState<ExceptionDecisionOption[]>([]);
  const [leftInVehicles, setLeftInVehicles] = useState<GoodsInVehicle[]>([]);
  const [postings, setPostings] = useState<PostingToDo[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [busyStopId, setBusyStopId] = useState<number | null>(null);
  const [busyPosting, setBusyPosting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Šta je poslednji potez uradio. Bez toga se ne zna da li se uopšte desio. */
  const [done, setDone] = useState<string | null>(null);

  /**
   * Otvorena tura. Drži se ŠIFRA, a stanice se dovlače uz nju — tako otvorena
   * tura može sama da se osvežava, a spisak tura ostaje lak: stanice se traže
   * tek kad ih neko zaista gleda.
   */
  /**
   * Šta se trenutno pita. Sve tri odluke traže tekst od čoveka, pa idu kroz isti
   * dijalog umesto kroz prozorčić pregledača — u njemu se nije videlo ni o kojoj
   * stanici je reč, ni koje su odluke moguće.
   */
  const [decidingOn, setDecidingOn] = useState<OpenException | null>(null);
  const [decision, setDecision] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const [abandoning, setAbandoning] = useState<TourProgress | null>(null);
  const [abandonReason, setAbandonReason] = useState('');

  /** Roba iz vozila o kojoj se odlučuje, i moguće odluke. */
  const [leftover, setLeftover] = useState<GoodsInVehicle | null>(null);
  const [leftoverChoice, setLeftoverChoice] = useState<string | null>(null);
  const [leftoverNote, setLeftoverNote] = useState('');
  const [leftoverOptions, setLeftoverOptions] = useState<ExceptionDecisionOption[]>([]);

  const [withdrawing, setWithdrawing] = useState<PostingToDo | null>(null);
  const [withdrawNote, setWithdrawNote] = useState('');

  const [openTourId, setOpenTourId] = useState<number | null>(null);
  const [openTour, setOpenTour] = useState<Detail | null>(null);

  const load = useCallback(async () => {
    try {
      const [progress, open, left] = await Promise.all([
        api.tourProgress(),
        api.openExceptions(),
        api.goodsInVehicles()
      ]);
      setTours(progress);
      setExceptions(open);
      setLeftInVehicles(left);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }

    // Nalozi se traže odvojeno: za njih treba ERP, pa njegov pad ne sme da
    // obori ceo ekran — izuzetak sa terena je važniji od knjiženja.
    try {
      setPostings(await api.postings());
    } catch {
      setPostings([]);
    }

    try {
      setDrivers(await api.drivers());
    } catch {
      setDrivers([]);
    }
  }, []);

  useEffect(() => {
    void load();
    void api.exceptionDecisions().then(setDecisions).catch(() => setDecisions([]));
    void api.leftoverDecisions().then(setLeftoverOptions).catch(() => setLeftoverOptions([]));

    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Otvorena tura se osvežava kao i ostatak ekrana. Bez toga bi operater gledao
  // stanje od pre nekoliko minuta baš dok čeka da vozač javi da je stigao.
  useEffect(() => {
    if (openTourId === null) {
      setOpenTour(null);
      return;
    }

    let stopped = false;

    const fetchDetail = () => {
      api.tourDetail(openTourId)
        .then((detail) => {
          if (!stopped) setOpenTour(detail);
        })
        .catch((e) => {
          if (!stopped) setError(e instanceof Error ? e.message : String(e));
        });
    };

    fetchDetail();
    const timer = window.setInterval(fetchDetail, REFRESH_MS);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [openTourId]);

  /**
   * Odbacuje turu koja se neće voziti.
   *
   * Traži razlog jer odbacivanje OSLOBAĐA ROBU — ona se posle toga ponovo nudi
   * za planiranje. Tura ostaje u bazi sa svim što je zapisano.
   */
  const abandon = async () => {
    if (!abandoning || abandonReason.trim() === '') return;

    try {
      await api.abandonTour(abandoning.tourId, abandonReason.trim());

      setDone(`Tura „${abandoning.name}“ je odbačena — roba sa nje se ponovo nudi za planiranje.`);
      setAbandoning(null);
      setAbandonReason('');
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const decideOnLeftover = async () => {
    if (!leftover || leftoverChoice === null) return;

    try {
      await api.decideOnLeftover(leftover.tourId, leftoverChoice, leftoverNote.trim() || null);

      setDone(`Zabeleženo šta je urađeno sa robom iz vozila ${leftover.vehicle}.`);
      setLeftover(null);
      setLeftoverChoice(null);
      setLeftoverNote('');
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const resolve = async () => {
    if (!decidingOn || decision === null) return;

    setBusyStopId(decidingOn.stopId);
    try {
      await api.resolveException(
        decidingOn.stopId, decision, note.trim() || null, signedIn.displayName);

      setExceptions((current) => current.filter((e) => e.stopId !== decidingOn.stopId));
      setDecidingOn(null);
      setDecision(null);
      setNote('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyStopId(null);
    }
  };

  /**
   * Operater potvrđuje da naloga u ERP-u više nema.
   *
   * Aplikacija to ne zaključuje sama: prolazna greška u čitanju izgleda isto kao
   * brisanje, a posledica bi bila da roba ostane odvojena bez pokrića — ili
   * obrnuto, da se pusti u prodaju iako stoji u magacinu rezervacija.
   */
  const withdraw = async () => {
    if (!withdrawing) return;

    setBusyPosting(withdrawing.documentNumber);
    try {
      await api.withdrawWrite(
        withdrawing.idempotencyKey, signedIn.displayName, withdrawNote.trim() || null);

      setPostings((current) =>
        current.filter((p) => p.documentNumber !== withdrawing.documentNumber));

      setWithdrawing(null);
      setWithdrawNote('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyPosting(null);
    }
  };

  return (
    <div className="monitor">
      {error && <div className="health health--bad">{error}</div>}
      {done && <div className="health health--ok">{done}</div>}

      <section className={exceptions.length > 0 ? 'panel panel--alert' : 'panel'}>
        <h2>
          Čeka odluku {exceptions.length > 0 && <span className="count">{exceptions.length}</span>}
        </h2>

        {exceptions.length === 0 ? (
          <p className="empty">Nema prijavljenih izuzetaka sa terena.</p>
        ) : (
          <>
            <ul className="exceptions">
              {exceptions.map((item) => (
                <li key={item.stopId} className="exception">
                  <p className="exception__where">
                    {item.stopName}
                    {item.city ? `, ${item.city}` : ''}
                    <span className="muted">
                      {' '}· {item.tourName}
                      {item.driver ? ` · ${item.driver}` : ''}
                      {item.reportedAt ? ` · ${formatTime(item.reportedAt)}` : ''}
                    </span>
                  </p>

                  <p className="exception__reason">{item.reason}</p>

                  {/* Brojevi stoje uz izuzetak jer je prvi korak poziv kupcu.
                      Bez njih bi ih operater tražio po ERP-u i odlagao odluku. */}
                  {item.phones?.length > 0 && (
                    <p className="exception__phone">
                      {item.phones.map((number) => (
                        <a key={number} href={`tel:${number}`}>{number}</a>
                      ))}
                    </p>
                  )}

                  {item.undeliveredItems.length > 0 && (
                    <p className="exception__items">
                      Nije predato: {item.undeliveredItems.join(', ')}
                    </p>
                  )}

                  <div className="exception__actions">
                    <button
                      type="button"
                      className="primary"
                      disabled={busyStopId === item.stopId}
                      onClick={() => { setDecidingOn(item); setDecision(null); setNote(''); }}
                    >
                      Odluči
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="panel">
        <h2>
          Ture danas
          {tours.some((t) => t.isStale) && (
            <span className="count"> · {tours.filter((t) => t.isStale).length} otvoreno od ranije</span>
          )}
        </h2>

        {tours.length === 0 ? (
          <p className="empty">Za danas nema nijedne ture.</p>
        ) : (
          <ul className="progress">
            {tours.map((tour) => (
              <li
                key={tour.tourId}
                className={`progress__row${openTourId === tour.tourId ? ' progress__row--open' : ''}`
                  + `${tour.isStale ? ' progress__row--stale' : ''}`}
              >
                {/* Ceo naziv je dugme: operater turu traži očima po imenu, pa bi
                    posebno dugme „otvori“ bilo još jedna meta da se promaši. */}
                <button
                  type="button"
                  className="progress__name progress__open"
                  onClick={() =>
                    setOpenTourId((current) => (current === tour.tourId ? null : tour.tourId))}
                >
                  {tour.name}
                  <span className="muted">
                    {' '}· {tour.driver ?? 'bez vozača'} · {tour.vehicle ?? 'bez vozila'}
                  </span>
                </button>

                <span className="progress__bar">
                  <span
                    className="progress__fill"
                    style={{
                      width: `${tour.totalStops > 0 ? (tour.finishedStops / tour.totalStops) * 100 : 0}%`
                    }}
                  />
                </span>

                <span className="progress__count">
                  {tour.finishedStops}/{tour.totalStops}
                </span>

                <span className="progress__flags">
                  {tour.isStale && (
                    <span className="flag flag--warn">
                      od {new Date(tour.date).toLocaleDateString('sr-RS')}
                    </span>
                  )}
                  {tour.openExceptions > 0 && (
                    <span className="flag flag--bad">{tour.openExceptions} izuzetak</span>
                  )}
                  {/* Tiho ne znači pokvareno — vozač u zgradi nema signal. Ali
                      operater treba da zna da javljanja nema. */}
                  {tour.isQuiet && <span className="flag flag--warn">nema javljanja</span>}
                  {tour.actualEnd && <span className="flag flag--ok">završena</span>}
                  {!tour.actualStart && (
                    <>
                      <span className="flag">nije krenula</span>
                      {/* Samo dok tura stoji. Kad vozilo krene, roba je u njemu i
                          to se rešava izuzetkom, ne brisanjem odluke. */}
                      <button
                        type="button"
                        className="link link--danger"
                        onClick={() => { setAbandoning(tour); setAbandonReason(''); }}
                      >
                        odbaci
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {openTourId !== null && (
        <section className="panel">
          {openTour
            ? <TourDetail tour={openTour} onClose={() => setOpenTourId(null)} />
            : <p className="muted">Učitavam turu…</p>}
        </section>
      )}

      {postings.length > 0 && (
        <section className="panel">
          <h2>
            Nalozi za knjiženje <span className="muted">· {postings.length}</span>
          </h2>

          <p className="note">
            Prenosi koje je aplikacija upisala u ERP. Nalog postoji i roba je
            prebačena, ali <strong>nije proknjižen</strong> — knjiženje radi čovek u
            ERP-u, jer taj klik ulazi u glavnu i pomoćne knjige. Kad se proknjiži,
            nalog sam nestaje sa spiska.
          </p>

          <ul className="rows">
            {postings.map((posting) => (
              <li key={posting.documentNumber}>
                <span className="row__name">Nalog {posting.documentNumber}</span>

                <span className="row__meta">
                  {posting.operation} · {posting.from} → {posting.to}
                  {' '}· {posting.articleCode} × {posting.quantity}
                  {posting.documentKey && ` · otpremnica ${posting.documentKey}`}
                </span>

                <span className="row__side">
                {posting.missingInErp ? (
                  <>
                    <span className="flag flag--bad">nema ga u ERP-u</span>

                    {/* Aplikacija ovo ne zaključuje sama: prolazna greška u
                        čitanju izgleda isto kao brisanje. Zato pita čoveka. */}
                    <button
                      type="button"
                      disabled={busyPosting === posting.documentNumber}
                      onClick={() => { setWithdrawing(posting); setWithdrawNote(''); }}
                    >
                      Poništen je, oslobodi robu
                    </button>
                  </>
                ) : (
                  <span className={posting.waitingDays > 1 ? 'flag flag--warn' : 'flag'}>
                    {posting.waitingDays === 0
                      ? 'danas'
                      : `${posting.waitingDays} ${posting.waitingDays === 1 ? 'dan' : 'dana'}`}
                  </span>
                )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {drivers.length > 0 && (
        <DriverLinks drivers={drivers} onChanged={() => void load()} />
      )}

      {decidingOn && (
        <Dialog
          title={`Odluka: ${decidingOn.stopName}`}
          description={
            <>
              <strong>{decidingOn.reason}</strong>
              {decidingOn.undeliveredItems.length > 0
                && <> · nije predato: {decidingOn.undeliveredItems.join(', ')}</>}
              <br />
              Stavka se ne zatvara sama — odluka govori šta se sa robom radi.
            </>
          }
          confirmLabel="Zabeleži odluku"
          busy={busyStopId === decidingOn.stopId}
          disabled={decision === null}
          onCancel={() => { setDecidingOn(null); setDecision(null); setNote(''); }}
          onConfirm={() => void resolve()}
        >
          <label>
            Šta se radi
            {decisions.length === 0 && (
              <span className="health health--bad">
                Spisak mogućih odluka nije stigao sa servera. Osveži stranicu.
              </span>
            )}
            <span className="choices">
              {decisions.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  aria-pressed={decision === d.code}
                  onClick={() => setDecision(d.code)}
                >
                  {d.label}
                </button>
              ))}
            </span>
          </label>

          <label>
            Beleška (može ostati prazno)
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="npr. kupac traži isporuku u subotu pre podne"
            />
          </label>
        </Dialog>
      )}

      {abandoning && (
        <Dialog
          title={`Odbaciti turu „${abandoning.name}“?`}
          description={
            <>Roba sa nje se oslobađa i ponovo nudi za planiranje. Tura ostaje
            zapisana sa svim stanicama — ne briše se.</>
          }
          confirmLabel="Odbaci turu"
          danger
          disabled={abandonReason.trim() === ''}
          onCancel={() => { setAbandoning(null); setAbandonReason(''); }}
          onConfirm={() => void abandon()}
        >
          <label>
            Razlog
            <input
              value={abandonReason}
              onChange={(e) => setAbandonReason(e.target.value)}
              placeholder="npr. vozilo u kvaru, roba ide sutra"
            />
          </label>
        </Dialog>
      )}

      {withdrawing && (
        <Dialog
          title={`Nalog ${withdrawing.documentNumber} je poništen u ERP-u?`}
          description={
            <>Roba prestaje da važi kao odvojena i može ponovo da se rezerviše.
            Zapis o upisu ostaje, pa se kasnije vidi šta se desilo.</>
          }
          confirmLabel="Oslobodi robu"
          danger
          busy={busyPosting === withdrawing.documentNumber}
          onCancel={() => { setWithdrawing(null); setWithdrawNote(''); }}
          onConfirm={() => void withdraw()}
        >
          <label>
            Beleška (može ostati prazno)
            <input
              value={withdrawNote}
              onChange={(e) => setWithdrawNote(e.target.value)}
              placeholder="npr. poslovođa javio da je greškom napravljen"
            />
          </label>
        </Dialog>
      )}

      {leftover && (
        <Dialog
          title={`Roba u vozilu ${leftover.vehicle}`}
          description={
            <>
              <strong>{leftover.quantity} kom</strong> sa ture {leftover.tourName} nije
              predato. Odluka se beleži i spisak se prazni — roba se za planiranje
              ionako već nudi, jer je tura završena.
            </>
          }
          confirmLabel="Zabeleži"
          disabled={leftoverChoice === null}
          onCancel={() => { setLeftover(null); setLeftoverChoice(null); setLeftoverNote(''); }}
          onConfirm={() => void decideOnLeftover()}
        >
          <label>
            Šta je urađeno
            {leftoverOptions.length === 0 ? (
              /* Prazan spisak nije prazan izbor nego kvar: server nije dao
                 moguće odluke. Ekran to kaže umesto da ostavi belinu ispod
                 naslova i dugme koje se ne da pritisnuti. */
              <span className="health health--bad">
                Spisak mogućih odluka nije stigao sa servera. Osveži stranicu; ako
                se ponovi, backend je stariji od ekrana.
              </span>
            ) : (
              <span className="choices">
                {leftoverOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    aria-pressed={leftoverChoice === option.code}
                    onClick={() => setLeftoverChoice(option.code)}
                  >
                    {option.label}
                  </button>
                ))}
              </span>
            )}
          </label>

          <label>
            Beleška (može ostati prazno)
            <input
              value={leftoverNote}
              onChange={(e) => setLeftoverNote(e.target.value)}
              placeholder="npr. ostaje u kombiju za Suboticu"
            />
          </label>
        </Dialog>
      )}

      {leftInVehicles.length > 0 && (
        <section className="panel">
          <h2>Ostalo u vozilima</h2>
          <p className="note">
            Roba sa završenih tura koja nije predata. Ujutru odlučuješ ostaje li u
            istom vozilu, vraća se u magacin, ili čeka drugo vozilo.
          </p>

          <ul className="rows">
            {leftInVehicles.map((entry) => (
              <li key={entry.tourId}>
                <span className="row__name">{entry.vehicle}</span>

                <span className="row__side">
                  <span className="flag flag--warn">{entry.quantity} kom</span>
                  <button
                    type="button"
                    onClick={() => {
                      setLeftover(entry);
                      setLeftoverChoice(null);
                      setLeftoverNote('');
                    }}
                  >
                    Odluči
                  </button>
                </span>

                <span className="row__meta">
                  {entry.tourName} · {formatDate(entry.date)} · {entry.items.join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('sr-RS');
}
